import { allocate, type Money } from './money.js';
import { formatNzDate, isCalendarDate } from './dates.js';

/**
 * The property domain — owner entities, properties, ownership splits and tenancies.
 *
 * PURE. Nothing here reads a clock or a database; `today` is passed in wherever it matters.
 *
 * This is the layer between people and money. A property has owners, in shares; a tenancy is a
 * letting of that property over a span of time; and every cent of rent that arrives has to
 * divide between those owners exactly, with no cent invented and none lost. `splitByOwnership`
 * below is where that happens, and it is the reason `share_bp` is basis points rather than a
 * percentage float.
 *
 * WHO LIVES THERE is not here and is not a column anywhere. It is answered by `person_roles`
 * with `tenancy_id` set — see docs/DATA_MODEL.md. A person is one row regardless of how many
 * tenancies they have been a tenant of.
 */

// ── Owner entities ───────────────────────────────────────────────────────────

/**
 * An owner is an ENTITY, not a person.
 *
 * "Solomona Holdings Ltd" and the "R & K Beauchamp Trust" hold the bank account, the GST
 * number and the trust ledger position. People attach to them by role — `owner_trustee`,
 * `owner_director`, `owner_signatory`. A sole natural-person owner is simply an entity with
 * one person attached, which keeps every downstream query uniform: there is never a branch
 * for "is this owner a human".
 */
export const OWNER_KINDS = ['natural_person', 'company', 'trust', 'partnership'] as const;
export type OwnerKind = (typeof OWNER_KINDS)[number];

export const OWNER_KIND_LABELS: Record<OwnerKind, string> = {
  natural_person: 'Individual',
  company: 'Company',
  trust: 'Trust',
  partnership: 'Partnership',
};

// ── Properties ───────────────────────────────────────────────────────────────

export const PROPERTY_TYPES = [
  'house',
  'unit',
  'apartment',
  'townhouse',
  'sleepout',
  'other',
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  house: 'House',
  unit: 'Unit',
  apartment: 'Apartment',
  townhouse: 'Townhouse',
  sleepout: 'Sleepout',
  other: 'Other',
};

/**
 * A New Zealand street address, in parts.
 *
 * Stored in parts rather than as one blob because the parts are what the product needs
 * separately: the suburb groups an inspection run, the city drives which Tenancy Services
 * office a bond goes to, and the street line alone is what fits in a table cell.
 *
 * Optional parts are `string | null` rather than optional keys — every one of them arrives
 * from a database row, where absent is null.
 */
export interface StreetParts {
  /** Flat or unit, written as a prefix in NZ: `4/9 Rimu Ave`. */
  unit?: string | null;
  streetNumber: string;
  street: string;
}

export interface Address extends StreetParts {
  suburb?: string | null;
  city: string;
  postcode?: string | null;
}

/**
 * `4/9 Rimu Ave` — the identifying line, and what a dense table shows.
 *
 * Takes only the street parts, not a whole `Address`. Naming a ledger account or labelling a
 * row needs the street line and nothing else, and a function that demands a city it will not
 * read forces every caller to select columns it does not want.
 */
export function streetLine(address: StreetParts): string {
  const number = address.unit ? `${address.unit}/${address.streetNumber}` : address.streetNumber;
  return `${number} ${address.street}`.trim();
}

/** `4/9 Rimu Ave, Glenfield, Auckland 0629` — the full postal form. */
export function formatAddress(address: Address): string {
  const cityLine = address.postcode ? `${address.city} ${address.postcode}` : address.city;
  return [streetLine(address), address.suburb, cityLine].filter(Boolean).join(', ');
}

/**
 * Healthy Homes Standards compliance, as tracked at this milestone.
 *
 * Deliberately coarse: a single assessed state, not the five standards (heating, insulation,
 * ventilation, moisture ingress and drainage, draught stopping) individually. Tracking them
 * separately is worth doing, but it belongs with the inspection module in M4, where the
 * evidence for each one gets attached. A coarse field that is honest about being coarse beats
 * five fields nobody fills in.
 */
export const HEALTHY_HOMES_STATES = ['compliant', 'not_compliant', 'unknown'] as const;
export type HealthyHomesState = (typeof HEALTHY_HOMES_STATES)[number];

export const HEALTHY_HOMES_LABELS: Record<HealthyHomesState, string> = {
  compliant: 'Compliant',
  not_compliant: 'Not compliant',
  unknown: 'Not assessed',
};

/**
 * The kinds of smoke alarm the Residential Tenancies (Smoke Alarms and Insulation) Regulations
 * distinguish between.
 *
 * `unknown` is a first-class value and the default, because it is the truthful answer for a
 * property whose alarms predate the agency taking it on. A required field with no honest option
 * gets filled in with a guess, and a guess here is a claim about a regulation.
 */
export const SMOKE_ALARM_TYPES = ['photoelectric', 'ionisation', 'dual_sensor', 'unknown'] as const;
export type SmokeAlarmType = (typeof SMOKE_ALARM_TYPES)[number];

export const SMOKE_ALARM_TYPE_LABELS: Record<SmokeAlarmType, string> = {
  photoelectric: 'Photoelectric',
  ionisation: 'Ionisation',
  dual_sensor: 'Dual sensor',
  unknown: 'Unknown type',
};

// ── Where the property is, and why it might not be anywhere ──────────────────

/**
 * What the address lookup last said about a property.
 *
 * ── WHY A STATUS AND NOT JUST A NULL COORDINATE ──────────────────────────────────────────────
 *
 * `latitude is null` says a property is not on the map. It does not say WHY, and the four
 * reasons want four different responses from a person:
 *
 *   `pending`     nobody has asked yet. Wait — a new or corrected address is resolved within a
 *                 few minutes by the `geocode` queue. Nothing to do.
 *   `located`     resolved to one point. On the map.
 *   `not_found`   the provider has no record of this address. THIS IS A TYPO, nearly always, and
 *                 nothing else on the property screen would show it.
 *   `ambiguous`   more than one address matched. Principle 3: never guess. A pin on the wrong
 *                 house is worse than no pin, so the property stays off the map deliberately.
 *   `failed`      the provider could not be reached, or answered with something unusable. Not
 *                 about this property at all, and it retries itself.
 *
 * Collapsing those four into "no coordinate" is what made an unmapped property unexplainable:
 * an operator corrected an address, the pin never came back, and there was nothing anywhere —
 * no error, no audit entry, no column — that said whether the address was wrong or the service
 * was down. Three of these four say what to do next; the fourth says explicitly to do nothing.
 */
export const GEOCODE_STATUSES = ['pending', 'located', 'not_found', 'ambiguous', 'failed'] as const;
export type GeocodeStatus = (typeof GEOCODE_STATUSES)[number];

/** The chip. Short enough for a table cell — the sentence is `GEOCODE_STATUS_HELP`. */
export const GEOCODE_STATUS_LABELS: Record<GeocodeStatus, string> = {
  pending: 'Awaiting lookup',
  located: 'Located',
  not_found: 'Address not found',
  ambiguous: 'Address not unique',
  failed: 'Lookup unavailable',
};

/**
 * What the operator should do about it, in one sentence.
 *
 * Written for a property manager rather than for us: it names the thing to check, or says
 * plainly that there is nothing to do. A status that leaves somebody guessing what to fix is
 * the problem this whole column exists to solve, so an unhelpful sentence here fails the point.
 */
export const GEOCODE_STATUS_HELP: Record<GeocodeStatus, string> = {
  pending:
    'Queued for the address lookup. New and corrected addresses are usually placed within a few minutes.',
  located: 'The address was matched to a single point and the property is drawn on the map.',
  not_found:
    'The address service has no record of this address. Check the street number, street name and suburb for a typo.',
  ambiguous:
    'The address matched more than one record, so no pin was set rather than the wrong one. A unit or flat number usually resolves it.',
  failed:
    'The address service could not be reached. Nothing is wrong with this property and it will be tried again automatically.',
};

/** True while nothing a person does would change the answer. */
export function geocodeIsOperatorFixable(status: GeocodeStatus): boolean {
  return status === 'not_found' || status === 'ambiguous';
}

/**
 * What the provider said about one address — the answer `applyGeocodeResult` records.
 *
 * A RESULT OR A REASON, never a bare null. The provider used to answer `null` for an outage
 * exactly as it did for a misspelled street, which meant the one fact worth keeping was thrown
 * away at the moment it was known and could not be reconstructed afterwards from anything.
 *
 * `detail` is the technical line — an HTTP status, a match count — shown under the sentence
 * above for whoever is actually debugging it. It must never carry the address: the row it is
 * written to already names the property, and repeating personal information into a second
 * column is how it ends up in a log.
 */
export type GeocodeOutcome =
  | { status: 'located'; latitude: number; longitude: number; provider: string }
  | { status: 'not_found' | 'ambiguous' | 'failed'; detail: string };

// ── Ownership ────────────────────────────────────────────────────────────────

/**
 * Shares are basis points: 1 bp = 0.01%, so a whole property is 10,000.
 *
 * Not a percentage, and emphatically not a float. A three-way split is 3334/3333/3333 — which
 * sums exactly — where 33.33% three times sums to 99.99% and quietly leaves a cent of somebody
 * else's money in the trust account every time rent arrives.
 */
export const TOTAL_SHARE_BP = 10_000;

export interface OwnershipShare {
  ownerId: string;
  shareBp: number;
}

/** `3334` → `33.34%`, `5000` → `50%`. Trailing zeros trimmed; it is read, not computed with. */
export function formatShare(shareBp: number): string {
  const whole = Math.trunc(shareBp / 100);
  const fraction = Math.abs(shareBp % 100);
  if (fraction === 0) return `${whole}%`;
  return `${whole}.${String(fraction).padStart(2, '0').replace(/0$/, '')}%`;
}

/**
 * Problems with a proposed set of ownership shares. Empty means it may be saved.
 *
 * The sum rule is the one that matters, and the message says how far out it is in the units
 * the operator typed rather than in basis points — "the shares add to 90%" is actionable,
 * "sum is 9000" is a debug print.
 */
export function ownershipIssues(shares: readonly OwnershipShare[]): string[] {
  const issues: string[] = [];

  if (shares.length === 0) {
    return ['A property needs at least one owner'];
  }

  const seen = new Set<string>();
  for (const share of shares) {
    if (seen.has(share.ownerId)) {
      issues.push('The same owner appears twice — combine their shares into one line');
    }
    seen.add(share.ownerId);

    if (!Number.isInteger(share.shareBp)) {
      issues.push('A share must be a whole number of basis points');
    } else if (share.shareBp <= 0) {
      issues.push('Every owner needs a share above zero — remove the owner instead');
    } else if (share.shareBp > TOTAL_SHARE_BP) {
      issues.push('No owner can hold more than 100%');
    }
  }

  const total = shares.reduce(
    (sum, share) => sum + (Number.isInteger(share.shareBp) ? share.shareBp : 0),
    0,
  );
  if (issues.length === 0 && total !== TOTAL_SHARE_BP) {
    const short = TOTAL_SHARE_BP - total;
    issues.push(
      `The shares add to ${formatShare(total)}, not 100% — ${
        short > 0
          ? `${formatShare(short)} is unallocated`
          : `${formatShare(-short)} too much has been allocated`
      }`,
    );
  }

  return issues;
}

export interface OwnerAmount {
  ownerId: string;
  amountCents: Money;
}

/**
 * Divide an amount between a property's owners. The parts always sum exactly to the total.
 *
 * REFUSES an invalid share set rather than doing its best with it, and that is the whole point
 * of the function. `allocate` normalises by the total weight, so shares of 5000/4000 would
 * happily split the full amount 5:9 and 4:9 — every cent paid out, none missing, and both
 * owners credited with the wrong number. A throw is the only safe answer to a set that does
 * not sum to 100%.
 */
export function splitByOwnership(total: Money, shares: readonly OwnershipShare[]): OwnerAmount[] {
  const issues = ownershipIssues(shares);
  if (issues.length > 0) {
    throw new RangeError(`Cannot split money across an invalid ownership: ${issues.join('; ')}`);
  }

  const amounts = allocate(
    total,
    shares.map((share) => BigInt(share.shareBp)),
  );

  return shares.map((share, index) => {
    const amountCents = amounts[index];
    // `allocate` returns one share per weight, so this cannot be missing. Saying so out loud
    // rather than with a `!`, because this is a money path — see the note in money.ts.
    if (amountCents === undefined) {
      throw new RangeError(`allocate() produced no share for owner ${share.ownerId}`);
    }
    return { ownerId: share.ownerId, amountCents };
  });
}

// ── Tenancies ────────────────────────────────────────────────────────────────

/** The far end of an open-ended span. Compares correctly as a string, which is the point. */
const FOREVER = '9999-12-31';

/**
 * A span of calendar days, closed at both ends.
 *
 * `endsOn` is the LAST day INCLUSIVE — the day the tenant hands the keys back is a day they
 * were still the tenant. An exclusive end would make a one-day tenancy describe itself as zero
 * days, and every rent calculation a day short.
 *
 * The same shape covers a tenancy's term, a management agreement and an ownership interest,
 * because they are the same fact: something was true from one date until another.
 */
export interface DateSpan {
  startsOn: string;
  /** Null while it runs. */
  endsOn?: string | null;
}

/** A tenancy's term. The same shape as any other span — see `DateSpan`. */
export type TenancyTerm = DateSpan;

export const SPAN_STATES = ['upcoming', 'active', 'ending', 'ended'] as const;
export type SpanState = (typeof SPAN_STATES)[number];

export const TENANCY_STATE_LABELS: Record<SpanState, string> = {
  upcoming: 'Upcoming',
  active: 'Active',
  ending: 'Ending',
  ended: 'Ended',
};

/** The same four states, said the way a property manager says them about a management. */
export const MANAGEMENT_STATE_LABELS: Record<SpanState, string> = {
  upcoming: 'Starts soon',
  active: 'Managed',
  ending: 'Ending',
  ended: 'No longer managed',
};

/**
 * Where today sits in a span.
 *
 * DERIVED, never stored. A status column would need something to flip it on the morning a
 * tenancy starts, and a nightly job that fails leaves the database asserting that a tenancy
 * which plainly began last Monday has not begun. The dates are the fact; the state is a reading
 * of them, and it is right the instant the clock passes midnight with nothing running.
 *
 * `ending` is running WITH A LEAVING DATE ALREADY SET — notice has been given and the day has
 * not arrived. It is not the same as `active` and reading it as such loses the one list a
 * property manager works from: the tenancies that need a final inspection, a bond refund and a
 * re-let, in the weeks before they need it rather than the morning after.
 *
 * It is NOT what a fixed term produces. `fixedTermEndsOn` is the date that was signed; `endsOn`
 * is the date somebody is actually leaving. A fixed term that nobody has given notice on rolls
 * into a periodic tenancy under the RTA, so it stays `active` — showing it as ending would put
 * every fixed-term tenancy in the agency on the vacating list.
 */
export function spanState(span: DateSpan, today: string): SpanState {
  const endsOn = span.endsOn ?? null;
  if (endsOn !== null && endsOn < today) return 'ended';
  // Before `ending`: something that has not started yet is upcoming whatever its end date says.
  if (span.startsOn > today) return 'upcoming';
  return endsOn === null ? 'active' : 'ending';
}

/** True when the span covers today — `active` or `ending`. Both mean somebody is living there. */
export function isRunning(span: DateSpan, today: string): boolean {
  const state = spanState(span, today);
  return state === 'active' || state === 'ending';
}

/** True when two spans cover any day in common. */
export function spansOverlap(a: DateSpan, b: DateSpan): boolean {
  return a.startsOn <= (b.endsOn ?? FOREVER) && b.startsOn <= (a.endsOn ?? FOREVER);
}

export interface TenancyProposal {
  term: TenancyTerm;
  /**
   * Every OTHER tenancy of the same property, whatever state it is in.
   *
   * Ended ones count. Occupancy is a fact about time, not about a status field: a tenancy that
   * finished in March still occupied the house in March, and a new one backdated into February
   * would claim the same weeks of rent for two different sets of tenants.
   */
  others: readonly (TenancyTerm & { label?: string })[];
}

/** Problems with a proposed tenancy. Empty means it may be saved. */
export function tenancyIssues(proposal: TenancyProposal): string[] {
  const issues: string[] = [];
  const { term, others } = proposal;

  if (!isCalendarDate(term.startsOn)) {
    return ['Give the tenancy a start date'];
  }

  const endsOn = term.endsOn ?? null;
  if (endsOn !== null && endsOn < term.startsOn) {
    issues.push('A tenancy cannot end before it starts');
  }

  for (const other of others) {
    if (spansOverlap(term, other)) {
      issues.push(
        `That overlaps ${other.label ?? 'another tenancy'} (${formatTerm(other)}) — a property can only be let to one tenancy at a time`,
      );
    }
  }

  return issues;
}

/**
 * `14/03/2026 to 30/09/2026`, or `from 14/03/2026` while it runs.
 *
 * Day-first, because this string is read by a property manager — it goes into the refusal when
 * a tenancy overlaps another, and into the one when a role is dated outside the record it
 * points at. Storage and comparison stay `YYYY-MM-DD`; only what reaches a human changes.
 */
export function formatTerm(span: DateSpan): string {
  const endsOn = span.endsOn ?? null;
  return endsOn === null
    ? `from ${formatNzDate(span.startsOn)}`
    : `${formatNzDate(span.startsOn)} to ${formatNzDate(endsOn)}`;
}
